// Attachment encryption (§14), all in the browser. Per-attachment key wrapped under the
// item_key; content sealed in 4 MB chunks (counter nonce + AAD); whole-plaintext SHA-384
// for integrity. Blob layout = nonce_base(8) ‖ sealed_chunk_0 ‖ … The server stores the
// opaque blob on the filesystem.
import { wasm } from "../crypto/core";
import { api, type AttachmentRow } from "../api/client";
import { b64decode, b64encode } from "../crypto/vault";

const EMPTY = new Uint8Array(0);
const enc = new TextEncoder();
const dec = new TextDecoder();

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

function eq(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

export function decryptFilename(itemKey: Uint8Array, encFilenameB64: string): string {
  try {
    return dec.decode(wasm.itemDecrypt(itemKey, EMPTY, b64decode(encFilenameB64)));
  } catch {
    return "(file)";
  }
}

/** Encrypt and upload a file as an attachment of an item. */
export async function uploadAttachment(itemId: string, itemKey: Uint8Array, file: File): Promise<void> {
  const data = new Uint8Array(await file.arrayBuffer());
  const attKey = wasm.generateKey();
  const wrapped = wasm.wrapSymmetric(itemKey, attKey, EMPTY);
  const nonceBase = wasm.attachmentNonceBase(); // also used as the AAD id (unique per attachment)
  const chunkSize = wasm.attachmentChunkSize();
  const count = Math.max(1, Math.ceil(data.length / chunkSize));

  const parts: Uint8Array[] = [nonceBase];
  for (let i = 0; i < count; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, data.length);
    parts.push(wasm.attachmentSealChunk(attKey, nonceBase, nonceBase, i, i === count - 1, data.subarray(start, end)));
  }
  const blob = concat(parts);

  const encHash = wasm.wrapSymmetric(itemKey, wasm.sha384(data), EMPTY);
  const encName = wasm.itemEncrypt(itemKey, EMPTY, enc.encode(file.name));
  const { id } = await api.createAttachment(itemId, {
    enc_filename: b64encode(encName),
    size_bytes: data.length,
    chunk_count: count,
    wrapped_attachment_key: b64encode(wrapped),
    enc_content_hash: b64encode(encHash),
  });
  await api.putAttachmentBlob(id, blob);
}

/** Download, decrypt and verify an attachment; returns the plaintext + filename. */
export async function downloadAttachment(att: AttachmentRow, itemKey: Uint8Array): Promise<{ name: string; data: Uint8Array }> {
  const attKey = wasm.unwrapSymmetric(itemKey, b64decode(att.wrapped_attachment_key), EMPTY);
  const blob = await api.getAttachmentBlob(att.id);
  const nonceBase = blob.subarray(0, 8);
  const chunkSize = wasm.attachmentChunkSize();
  let off = 8;
  const out: Uint8Array[] = [];
  for (let i = 0; i < att.chunk_count; i++) {
    const isFinal = i === att.chunk_count - 1;
    const plainLen = isFinal ? att.size_bytes - i * chunkSize : chunkSize;
    const sealedLen = plainLen + 16; // GCM tag
    const sealed = blob.subarray(off, off + sealedLen);
    off += sealedLen;
    out.push(wasm.attachmentOpenChunk(attKey, nonceBase, nonceBase, i, isFinal, sealed));
  }
  const data = concat(out);
  const expected = wasm.unwrapSymmetric(itemKey, b64decode(att.enc_content_hash), EMPTY);
  if (!eq(expected, wasm.sha384(data))) throw new Error("attachment integrity check failed");
  return { name: decryptFilename(itemKey, att.enc_filename), data };
}
